// AnnouncementsScreen.js - avec gestion des annonces expirées
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Image,
  SafeAreaView,
  ActivityIndicator,
  Platform,
  Modal,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../screens/ThemeContext';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

const StatusBar = Platform.OS === 'web' ? () => null : require('react-native').StatusBar;

export default function AnnouncementsScreen({ navigation }) {
  const { theme } = useTheme();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [infoModalVisible, setInfoModalVisible] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const q = query(
        collection(db, 'announcements'),
        where('status', '==', 'active'),
        where('etat', '==', 'approved'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      let announcementsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filtrer les annonces expirées
      const now = new Date();
      announcementsData = announcementsData.filter(announcement => {
        // Vérifier si l'annonce a un plan d'abonnement avec une date d'expiration
        if (announcement.subscriptionPlan?.expirationDate) {
          // Convertir la date d'expiration en objet Date JavaScript
          const expirationDate = announcement.subscriptionPlan.expirationDate.toDate 
            ? announcement.subscriptionPlan.expirationDate.toDate() 
            : new Date(announcement.subscriptionPlan.expirationDate);
            
          // Garder l'annonce uniquement si la date d'expiration est dans le futur
          return expirationDate > now;
        }
        
        // Si pas de date d'expiration définie, vérifier la date de fin
        if (announcement.dateFin) {
          const finDate = announcement.dateFin.toDate 
            ? announcement.dateFin.toDate() 
            : new Date(announcement.dateFin);
            
          return finDate > now;
        }
        
        // Par défaut, inclure l'annonce si aucune date n'est définie
        return true;
      });
      
      setAnnouncements(announcementsData);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderConcours = (concours) => (
    <View style={styles.concoursContainer}>
      {concours.map((item, index) => (
        <View key={index} style={[styles.concoursBadge, { backgroundColor: theme.background }]}>
          <Text style={[styles.concoursText, { color: theme.primary }]}>{item}</Text>
        </View>
      ))}
    </View>
  );

  const formatDate = (date) => {
    if (!date) return '';
    
    // Gérer à la fois les timestamps Firestore et les objets Date
    const dateObj = date.toDate ? date.toDate() : new Date(date.seconds * 1000);
    
    return dateObj.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredAnnouncements = searchQuery
    ? announcements.filter(
        ann => 
          ann.titre.toLowerCase().includes(searchQuery.toLowerCase()) ||
          ann.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          ann.concours.some(c => c.toLowerCase().includes(searchQuery.toLowerCase())) ||
          ann.centerInfo.centerName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : announcements;

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.primary }]}>
      <StatusBar barStyle="light-content" backgroundColor={theme.primary} />
      <ScrollView style={[styles.container, { backgroundColor: theme.background }]}>
        {/* Header */}
        <LinearGradient
          colors={[theme.primary, theme.primaryDark]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.header}
        >
          <View style={styles.headerRow}>
            <Text style={styles.headerTitle}>Annonces</Text>
            <TouchableOpacity
              style={styles.infoButton}
              onPress={() => setInfoModalVisible(true)}
            >
              <MaterialCommunityIcons name="information-outline" size={24} color="#fff" />
            </TouchableOpacity>
          </View>
        </LinearGradient>

        {/* Search Section */}
        <View style={[styles.searchSection, { backgroundColor: theme.surface }]}>
          <View style={[styles.searchBar, { backgroundColor: theme.background }]}>
            <MaterialCommunityIcons name="magnify" size={24} color={theme.textSecondary} />
            <TextInput
              style={[styles.searchInput, { color: theme.text }]}
              placeholder="Rechercher une formation..."
              placeholderTextColor={theme.textSecondary}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>
        </View>

        {/* Announcements List */}
        <View style={styles.announcementsContainer}>
          {loading ? (
            <ActivityIndicator size="large" color={theme.primary} style={styles.loader} />
          ) : filteredAnnouncements.length > 0 ? (
            filteredAnnouncements.map((announcement) => (
              <TouchableOpacity
                key={announcement.id}
                style={[styles.announcementCard, { backgroundColor: theme.surface, borderColor: theme.border }]}
                onPress={() => navigation.navigate('CentreScreenDetailleUser', { announcement })}
              >
                <Image
                  source={{ uri: announcement.imageUrl }}
                  style={styles.announcementImage}
                />
                <View style={styles.announcementContent}>
                  <View style={styles.centerInfo}>
                    <Text style={[styles.centerName, { color: theme.text }]}>
                      {announcement.centerInfo.centerName}
                    </Text>
                  </View>
                  <Text style={[styles.announcementTitle, { color: theme.text }]}>
                    {announcement.titre}
                  </Text>
                  <Text style={[styles.announcementDescription, { color: theme.textSecondary }]} numberOfLines={2}>
                    {announcement.description}
                  </Text>
                  {renderConcours(announcement.concours)}
                  {/* <View style={styles.dateContainer}>
                    <MaterialCommunityIcons name="calendar" size={16} color={theme.textSecondary} />
                    <Text style={[styles.dateText, { color: theme.textSecondary }]}>
                      Du {formatDate(announcement.dateDebut)} au {formatDate(announcement.dateFin)}
                    </Text>
                  </View> */}
                </View>
              </TouchableOpacity>
            ))
          ) : (
            <Text style={[styles.emptyText, { color: theme.textSecondary }]}>
              Aucune annonce disponible
            </Text>
          )}
        </View>
        <View style={styles.bottomSpace} />
      </ScrollView>

      {/* Modal d'information pour les centres de prépa */}
      <Modal
        visible={infoModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setInfoModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.surface }]}>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setInfoModalVisible(false)}
            >
              <MaterialCommunityIcons name="close" size={24} color={theme.textSecondary} />
            </TouchableOpacity>

            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.modalHeader}>
                <View style={[styles.modalIconContainer, { backgroundColor: theme.primary + '20' }]}>
                  <MaterialCommunityIcons name="school-outline" size={40} color={theme.primary} />
                </View>
              </View>

              <Text style={[styles.modalTitle, { color: theme.text }]}>
                Vous êtes un Centre de Préparation ?
              </Text>

              <Text style={[styles.modalDescription, { color: theme.textSecondary }]}>
                Faites-vous connaître auprès de milliers d'étudiants en publiant vos annonces de formation sur MyConcours !
              </Text>

              <View style={[styles.tarifSection, { backgroundColor: theme.background }]}>
                <Text style={[styles.tarifTitle, { color: theme.text }]}>
                  Nos forfaits publicitaires
                </Text>

                <View style={styles.tarifItem}>
                  <View style={[styles.tarifBadge, { backgroundColor: '#4CAF50' }]}>
                    <Text style={styles.tarifBadgeText}>5 Jours</Text>
                  </View>
                  <Text style={[styles.tarifPrice, { color: theme.text }]}>15 000 XAF</Text>
                </View>

                <View style={styles.tarifItem}>
                  <View style={[styles.tarifBadge, { backgroundColor: '#2196F3' }]}>
                    <Text style={styles.tarifBadgeText}>15 Jours</Text>
                  </View>
                  <Text style={[styles.tarifPrice, { color: theme.text }]}>35 000 XAF</Text>
                </View>

                <View style={styles.tarifItem}>
                  <View style={[styles.tarifBadge, { backgroundColor: '#9C27B0' }]}>
                    <Text style={styles.tarifBadgeText}>30 Jours</Text>
                  </View>
                  <Text style={[styles.tarifPrice, { color: theme.text }]}>100 000 XAF</Text>
                </View>

                <View style={styles.tarifItem}>
                  <View style={[styles.tarifBadge, { backgroundColor: '#FF9800' }]}>
                    <Text style={styles.tarifBadgeText}>3 Mois</Text>
                  </View>
                  <Text style={[styles.tarifPrice, { color: theme.text }]}>300 000 XAF</Text>
                </View>
              </View>

              <View style={styles.stepsContainer}>
                <Text style={[styles.stepsTitle, { color: theme.text }]}>
                  Comment publier ?
                </Text>

                <View style={styles.stepItem}>
                  <View style={[styles.stepNumber, { backgroundColor: theme.primary }]}>
                    <Text style={styles.stepNumberText}>1</Text>
                  </View>
                  <Text style={[styles.stepText, { color: theme.textSecondary }]}>
                    Créez un compte "Centre de Préparation"
                  </Text>
                </View>

                <View style={styles.stepItem}>
                  <View style={[styles.stepNumber, { backgroundColor: theme.primary }]}>
                    <Text style={styles.stepNumberText}>2</Text>
                  </View>
                  <Text style={[styles.stepText, { color: theme.textSecondary }]}>
                    Connectez-vous à l'espace Centre
                  </Text>
                </View>

                <View style={styles.stepItem}>
                  <View style={[styles.stepNumber, { backgroundColor: theme.primary }]}>
                    <Text style={styles.stepNumberText}>3</Text>
                  </View>
                  <Text style={[styles.stepText, { color: theme.textSecondary }]}>
                    Créez et publiez votre annonce
                  </Text>
                </View>
              </View>

              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: theme.primary }]}
                onPress={() => setInfoModalVisible(false)}
              >
                <Text style={styles.modalButtonText}>J'ai compris</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  header: {
    paddingTop: 20,
    paddingBottom: 30,
    paddingHorizontal: 20,
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
    flex: 1,
  },
  infoButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  searchSection: {
    padding: 20,
    margin: 20,
    marginTop: -20,
    borderRadius: 20,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.05,
    shadowRadius: 15,
    elevation: 5,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    paddingHorizontal: 15,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    marginLeft: 10,
    fontSize: 16,
  },
  announcementsContainer: {
    padding: 20,
  },
  announcementCard: {
    borderRadius: 16,
    marginBottom: 20,
    borderWidth: 1,
    overflow: 'hidden',
  },
  announcementImage: {
    width: '100%',
    height: 200,
    backgroundColor: '#f0f0f0',
  },
  announcementContent: {
    padding: 15,
  },
  centerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  centerName: {
    fontSize: 14,
    fontWeight: '600',
  },
  announcementTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  announcementDescription: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
  },
  concoursContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  concoursBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  concoursText: {
    fontSize: 12,
    fontWeight: '500',
  },
  dateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateText: {
    marginLeft: 6,
    fontSize: 12,
  },
  loader: {
    marginTop: 40,
  },
  emptyText: {
    textAlign: 'center',
    marginTop: 40,
    fontSize: 16,
  },
  bottomSpace: {
    height: 100,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
    borderRadius: 24,
    padding: 24,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalCloseButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    padding: 4,
    zIndex: 10,
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 12,
  },
  modalDescription: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 20,
  },
  tarifSection: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 20,
  },
  tarifTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  tarifItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
  },
  tarifBadge: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 20,
  },
  tarifBadgeText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  tarifPrice: {
    fontSize: 16,
    fontWeight: '700',
  },
  stepsContainer: {
    marginBottom: 20,
  },
  stepsTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  stepNumberText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  stepText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  modalButton: {
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: 'center',
  },
  modalButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});